import { BaseModel } from '@shared/base.model';
import { VerificationRule } from '@shared/components/files/shared/verification-rules.enum';
import { Organization } from '@shared/models/organization/organization.model';
import { Country } from '@shared/services/country/country.model';

import { ConnectionsProfile } from './connections-profile.model';
import { ProgramApplicationStageConfiguration } from './program-application-stage-configuration.model';
import { ProgramApplication } from './program-application.model';
import { ProgramPermissions } from './program-permissions.model';
import { ProgramPreQualificationCriteria } from './program-pre-qualification-criteria.model';
import { OrganizationFinancialModel } from '@project/detail/financials/organization-financial-model/organization-financial-model.model';
import { ProgramMilestonePolicy } from '@program/shared/program-milestone-policy.model';

export enum PROGRAM_TYPE {
  PUBLIC_NO_PRE_QUALIFICATION = 'PUBLIC_NO_PRE_QUALIFICATION',
  PUBLIC_PRE_QUALIFICATION = 'PUBLIC_PRE_QUALIFICATION',
  INVITE_ONLY_NO_PRE_QUALIFICATION = 'INVITE_ONLY_NO_PRE_QUALIFICATION',
  INVITE_ONLY_PRE_QUALIFICATION = 'INVITE_ONLY_PRE_QUALIFICATION'
}

export enum ProgramSubmissionType {
  PROJECT = 'PROJECT',
  C_I = 'C_I',
  SCC = 'SCC'
}

export enum FinancialModelTemplate {
  ODYSSEY = 'ODYSSEY',
}

export class Program extends BaseModel<Program> {
  beginDateTime: Date;
  bidReviewDateTime: Date;
  connectionsProfile: ConnectionsProfile;
  country: Country;
  description: string;
  eligibleProjectTypes: string[];
  endDateTime: Date;
  financialModel: OrganizationFinancialModel;
  programMilestonePolicy: ProgramMilestonePolicy;
  guideUrlParam: string;
  html: string;
  inviteOnly: boolean;
  name: string;
  preQualEndDateTime: Date;
  programApplications: ProgramApplication[];
  programApplicationStageConfigurations: ProgramApplicationStageConfiguration[];
  programPermissions: ProgramPermissions;
  programPreQualificationCriteria: ProgramPreQualificationCriteria[] = [];
  projectApplicationFormRequired: boolean;
  /**
   * Names a code-defined project-creation workflow; null means default behavior. SYSTEM_ADMIN-only.
   * MUST be mapped in the constructor below — it is hand-mapped with no spread, so an unmapped
   * field arrives `undefined` on every read and the whole workflow layer is silently inert.
   */
  projectCreationWorkflowKey: string;
  projectEvalConfigId: string;
  projectGroupBusinessPlanFormRequired: boolean;
  projectGroupChecklistFormRequired: boolean;
  requirePreQualification: boolean;
  sponsor: Organization;
  submissionType: ProgramSubmissionType;
  verificationRule: VerificationRule;

  constructor(programInfo: any) {
    super();
    this.id = programInfo.id;
    this.beginDateTime = programInfo.beginDateTime;
    this.bidReviewDateTime = programInfo.bidReviewDateTime;
    this.connectionsProfile = programInfo.connectionsProfile;
    this.country = programInfo.country;
    this.description = programInfo.description;
    this.eligibleProjectTypes = programInfo.eligibleProjectTypes || [];
    this.endDateTime = programInfo.endDateTime;
    this.guideUrlParam = programInfo.guideUrlParam;
    this.html = programInfo.html;
    this.inviteOnly = programInfo.inviteOnly;
    this.name = programInfo.name;
    this.preQualEndDateTime = programInfo.preQualEndDateTime;
    this.programApplications = programInfo.programApplications;
    this.programApplicationStageConfigurations = programInfo.programApplicationStageConfigurations;
    this.programPermissions = programInfo.programPermissions;
    this.projectApplicationFormRequired = programInfo.projectApplicationFormRequired;
    this.projectCreationWorkflowKey = programInfo.projectCreationWorkflowKey;
    this.projectEvalConfigId = programInfo.projectEvalConfigId;
    this.projectGroupBusinessPlanFormRequired = programInfo.projectGroupBusinessPlanFormRequired;
    this.projectGroupChecklistFormRequired = programInfo.projectGroupChecklistFormRequired;
    this.sponsor = new Organization(programInfo.sponsor || {});
    this.submissionType = programInfo.submissionType;
    this.verificationRule = programInfo.verificationRule;

    if (programInfo.programPreQualificationCriteria) {
      programInfo.programPreQualificationCriteria.forEach(criterion => {
        this.programPreQualificationCriteria.push(new ProgramPreQualificationCriteria(criterion));
      });
    }
    this.requirePreQualification = programInfo.requirePreQualification;
    this.financialModel = programInfo.financialModel;
    this.programMilestonePolicy = programInfo.programMilestonePolicy;
  }

  get link() {
    return `/oes/finance/programs/detail/${this.id}/`;
  }
}
